import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fileService } from '../services/api';
import { filterFiles, sortFiles, extractVirtualFolders, isImage, isRawImage, isFolder } from '../utils/fileUtils';

export const useFiles = (isAuthenticated = false) => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [navigationHistory, setNavigationHistory] = useState(['']);
  const [fromCache, setFromCache] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [rootFolders, setRootFolders] = useState([]);
  const [rootFoldersLoading, setRootFoldersLoading] = useState(false);
  const [rootFoldersLoaded, setRootFoldersLoaded] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagResults, setTagResults] = useState([]);
  const [tagResultsLoading, setTagResultsLoading] = useState(false);
  const [tagResultsError, setTagResultsError] = useState(null);
  
  // Estados para tags de carpetas
  const [selectedFolderTag, setSelectedFolderTag] = useState(null);
  const [folderTagResults, setFolderTagResults] = useState([]);
  const [folderTagResultsLoading, setFolderTagResultsLoading] = useState(false);
  const [folderTagResultsError, setFolderTagResultsError] = useState(null);
  
  // Ref para evitar múltiples cargas simultáneas
  const loadingRef = useRef(false);
  const lastLoadedPath = useRef('');
  const lastTagSearchRef = useRef({ selectedTag: null, selectedFolderTag: null });
  // Refs para leer valores actuales sin incluirlos en dependencias
  const selectedFolderTagRef = useRef(null);
  const folderTagResultsRef = useRef([]);
  
  // Actualizar refs cuando cambian los estados
  useEffect(() => {
    selectedFolderTagRef.current = selectedFolderTag;
  }, [selectedFolderTag]);
  
  useEffect(() => {
    folderTagResultsRef.current = folderTagResults;
  }, [folderTagResults]);

  // Calcular estadísticas locales para imágenes y archivos RAW
  const localStats = useMemo(() => {
    if (!files || files.length === 0) return null;
    
    let imageFiles = 0;
    let rawFiles = 0;
    
    files.forEach(file => {
      const fileName = file.Key || file.name;
      if (fileName && !isFolder(file)) {
        if (isImage(fileName)) {
          if (isRawImage(fileName)) {
            rawFiles++;
          } else {
            imageFiles++;
          }
        }
      }
    });
    
    return {
      ...stats, // Mantener las estadísticas del servidor
      imageFiles,
      rawFiles
    };
  }, [files, stats]);

  // Filtrar archivos basura (._ y .__), pero NO carpetas reales
  const filterJunkFiles = (files) => {
    return files.filter(file => {
      const fileName = file.Key || file.name || '';
      
      // Si es carpeta real (termina en /), no filtrar
      if (fileName.endsWith('/')) {
        return true;
      }
      
      // Filtrar archivos que empiecen con ._ o .__
      const baseName = fileName.split('/').pop(); // Solo el nombre del archivo, no el path
      return !baseName.startsWith('._') && !baseName.startsWith('.__') && baseName !== '.DS_Store';
    });
  };

  // Cargar archivos desde la API (optimizado con caché)
  const loadFiles = useCallback(async (folderPath = '', forceRefresh = false) => {
    if (!isAuthenticated) return;
    
    // Evitar múltiples cargas simultáneas de la misma carpeta SOLO si ya está cargando
    if (loadingRef.current && lastLoadedPath.current === folderPath && !forceRefresh) {
      console.log(`⏭️ Evitando carga duplicada para: "${folderPath || 'root'}"`);
      return;
    }
    
    // Si forceRefresh es true, resetear flags para forzar recarga completa
    if (forceRefresh) {
      loadingRef.current = false;
      lastLoadedPath.current = null;
      console.log(`🔄 Forzando recarga completa para: "${folderPath || 'root'}"`);
    }
    
    loadingRef.current = true;
    lastLoadedPath.current = folderPath;
    setLoading(true);
    setError(null);
    
    try {
      console.log(`📂 Cargando archivos para carpeta: "${folderPath || 'root'}"${forceRefresh ? ' (forzando refrescar)' : ''}`);
      const result = await fileService.getFiles(folderPath, forceRefresh);
      console.log(`✅ Archivos cargados: ${result.data?.length || 0} elementos`);
      setFiles(filterJunkFiles(result.data || []));
      setFromCache(result.fromCache);
      setHasLoadedOnce(true);
    } catch (err) {
      setError(err.message);
      console.error('Error cargando archivos:', err);
      
      // En caso de error, limpiar caché para esa carpeta
      fileService.clearCache(folderPath);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [isAuthenticated]);

  // Cargar estadísticas
  const loadStats = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const statsData = await fileService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  }, [isAuthenticated]);

  // Cargar catálogo de tags
  const loadTags = useCallback(async (limit = 50) => {
    if (!isAuthenticated) return;

    setTagsLoading(true);
    try {
      const catalog = await fileService.getTagsCatalog(limit);
      setTags(Array.isArray(catalog) ? catalog : []);
    } catch (err) {
      console.error('Error cargando catálogo de tags:', err);
      setTags([]);
    } finally {
      setTagsLoading(false);
    }
  }, [isAuthenticated]);

  // Método fallback para cargar carpetas raíz extrayendo de archivos
  const loadRootFoldersFallback = useCallback(async () => {
    try {
      console.log('📂 Fallback: extrayendo carpetas de listado de archivos...');
      const result = await fileService.getFiles('');
      const files = result.data || [];
      
      const foldersSet = new Set();
      files.forEach(file => {
        const path = file.Key || file.name || '';
        if (path.includes('/')) {
          const rootFolder = path.split('/')[0];
          if (rootFolder) {
            foldersSet.add(rootFolder);
          }
        }
      });
      
      const extractedFolders = Array.from(foldersSet).sort();
      console.log(`✅ Carpetas extraídas via fallback (${extractedFolders.length}):`, extractedFolders.join(', '));
      setRootFolders(extractedFolders);
      setRootFoldersLoaded(true);
    } catch (fallbackErr) {
      console.error('❌ Error en fallback:', fallbackErr);
      setRootFolders([]); // Limpiar en caso de error total
      setRootFoldersLoaded(true); // Marcar como loaded aunque haya fallado
    }
  }, []);

  // Cargar carpetas raíz
  const loadRootFolders = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('❌ No está autenticado, omitiendo carga de carpetas raíz');
      return;
    }
    
    setRootFoldersLoading(true);
    setRootFoldersLoaded(false);
    
    try {
      console.log('📁 Cargando carpetas raíz para el área principal...');
      const folders = await fileService.getRootFolders();
      console.log(`✅ Carpetas raíz recibidas de API: ${folders.length} carpetas:`, folders);
      setRootFolders(folders);
      setRootFoldersLoaded(true);
      console.log(`🔄 Estado rootFolders actualizado con: ${folders.join(', ')}`);
      
      // Si no hay carpetas, intentar fallback
      if (folders.length === 0) {
        console.log('⚠️ No se encontraron carpetas raíz, intentando fallback...');
        await loadRootFoldersFallback();
      }
    } catch (err) {
      console.error('❌ Error cargando carpetas raíz:', err);
      console.log('🔄 Intentando método fallback...');
      await loadRootFoldersFallback();
    } finally {
      setRootFoldersLoading(false);
    }
  }, [isAuthenticated, loadRootFoldersFallback]);

  // Cargar tags al autenticarse
  useEffect(() => {
    if (isAuthenticated) {
      loadTags();
    } else {
      setTags([]);
      setSelectedTag(null);
      setTagResults([]);
    }
  }, [isAuthenticated, loadTags]);

  // Función para normalizar tags (igual que en el backend)
  const normalizeTag = (tag) => {
    if (!tag || typeof tag !== 'string') return '';
    return tag.toLowerCase().trim().replace(/\s+/g, '-');
  };

  // Cargar archivos asociados a un tag
  const loadFilesByTag = useCallback(async (tag, options = {}) => {
    if (!isAuthenticated || !tag) return;

    // Normalizar el tag correctamente (reemplazar espacios con guiones)
    const normalizedTag = normalizeTag(tag);
    
    // Verificar si ya se hizo esta búsqueda para evitar loops
    const currentSearch = {
      selectedTag: normalizedTag,
      selectedFolderTag: selectedFolderTagRef.current
    };
    
    if (
      lastTagSearchRef.current.selectedTag === currentSearch.selectedTag &&
      lastTagSearchRef.current.selectedFolderTag === currentSearch.selectedFolderTag
    ) {
      console.log('⏭️ Búsqueda ya realizada, evitando duplicado');
      return;
    }
    
    lastTagSearchRef.current = currentSearch;
    
    setSelectedTag(normalizedTag);
    setTagResultsLoading(true);
    setTagResultsError(null);

    try {
      // Si hay un tag de carpeta seleccionado y hay carpetas cargadas, buscar archivos dentro de esas carpetas
      // (a menos que se especifique explícitamente no hacerlo con options.skipFolderSearch)
      const currentFolderTag = selectedFolderTagRef.current;
      const currentFolderResults = folderTagResultsRef.current;
      
      if (currentFolderTag && currentFolderResults.length > 0 && !options.skipFolderSearch) {
        console.log(`🔍 Buscando archivos con tag "${normalizedTag}" dentro de ${currentFolderResults.length} carpetas (tag de carpeta: "${currentFolderTag}")...`);
        const allFiles = [];
        
        // Buscar archivos en cada carpeta en paralelo
        const searchPromises = currentFolderResults.map(async (folder) => {
          const folderPath = folder.Key || folder.name || '';
          if (!folderPath) return [];
          
          try {
            const response = await fileService.getFilesByTag(normalizedTag, 200, folderPath);
            return filterJunkFiles(response.files || []);
          } catch (err) {
            console.warn(`⚠️ Error buscando archivos en carpeta "${folderPath}":`, err);
            return [];
          }
        });
        
        const results = await Promise.all(searchPromises);
        // Combinar todos los archivos encontrados
        results.forEach(files => {
          allFiles.push(...files);
        });
        
        // Eliminar duplicados (por si acaso)
        const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.Key || file.name, file])).values());
        
        console.log(`✅ Archivos encontrados: ${uniqueFiles.length} archivos con tag "${normalizedTag}" dentro de las carpetas`);
        setSelectedTag(normalizedTag);
        setTagResults(uniqueFiles);
      } else if (options.folders && options.folders.length > 0) {
        // Si se pasan carpetas explícitamente en options (para casos especiales)
        console.log(`🔍 Buscando archivos con tag "${normalizedTag}" dentro de ${options.folders.length} carpetas...`);
        const allFiles = [];
        
        const searchPromises = options.folders.map(async (folder) => {
          const folderPath = folder.Key || folder.name || '';
          if (!folderPath) return [];
          
          try {
            const response = await fileService.getFilesByTag(normalizedTag, 200, folderPath);
            return filterJunkFiles(response.files || []);
          } catch (err) {
            console.warn(`⚠️ Error buscando archivos en carpeta "${folderPath}":`, err);
            return [];
          }
        });
        
        const results = await Promise.all(searchPromises);
        results.forEach(files => {
          allFiles.push(...files);
        });
        
        const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.Key || file.name, file])).values());
        
        console.log(`✅ Archivos encontrados: ${uniqueFiles.length} archivos con tag "${normalizedTag}" dentro de las carpetas`);
        setSelectedTag(normalizedTag);
        setTagResults(uniqueFiles);
      } else {
        // Comportamiento normal: buscar en currentPath o globalmente
        const prefix = options.prefix !== undefined ? options.prefix : (currentPath || '');
        
        const response = await fileService.getFilesByTag(normalizedTag, options.limit || 200, prefix);
        const filesByTag = filterJunkFiles(response.files || []);
        setSelectedTag(response.tag || normalizedTag);
        setTagResults(filesByTag);
      }
    } catch (err) {
      console.error('Error cargando archivos por tag:', err);
      setSelectedTag(normalizedTag);
      setTagResults([]);
      setTagResultsError(err.message);
    } finally {
      setTagResultsLoading(false);
    }
  }, [isAuthenticated, currentPath]);

  const clearTagFilter = useCallback(() => {
    setSelectedTag(null);
    setTagResults([]);
    setTagResultsError(null);
  }, []);

  // Cargar carpetas asociadas a un tag de carpeta
  const loadFilesByFolderTag = useCallback(async (tag) => {
    if (!isAuthenticated || !tag) return;

    // Normalizar el tag correctamente (reemplazar espacios con guiones)
    const normalizedTag = normalizeTag(tag);
    setSelectedFolderTag(normalizedTag);
    setFolderTagResultsLoading(true);
    setFolderTagResultsError(null);
    // NO limpiar el filtro de archivos - permitir que ambos filtros coexistan

    try {
      console.log(`🔍 Cargando carpetas por tag: "${normalizedTag}"`);
      const response = await fileService.getFoldersByTag(normalizedTag);
      console.log(`📦 Respuesta del backend:`, response);
      const foldersByTag = filterJunkFiles(response.folders || []);
      
      // Formatear las carpetas para que tengan el mismo formato que los archivos normales
      const formattedFolders = foldersByTag.map(folder => {
        const fileName = folder.Key || folder.name || '';
        // Extraer solo el nombre de la carpeta (sin el path completo)
        const folderName = fileName.endsWith('/') 
          ? fileName.slice(0, -1).split('/').pop() 
          : fileName.split('/').pop();
        
        return {
          ...folder,
          Key: fileName,
          displayName: folderName || fileName,
          type: 'folder',
          isVirtualFolder: false
        };
      });
      
      console.log(`✅ Carpetas filtradas: ${formattedFolders.length} carpetas encontradas`);
      setSelectedFolderTag(response.tag || normalizedTag);
      setFolderTagResults(formattedFolders);
      
      // Si hay un tag de archivo seleccionado, buscar archivos dentro de estas carpetas
      // (Esto se manejará en el useEffect que observa selectedTag y folderTagResults)
    } catch (err) {
      console.error('❌ Error cargando carpetas por tag:', err);
      setSelectedFolderTag(normalizedTag);
      setFolderTagResults([]);
      setFolderTagResultsError(err.message);
    } finally {
      setFolderTagResultsLoading(false);
    }
  }, [isAuthenticated, selectedTag]);

  const clearFolderTagFilter = useCallback(() => {
    setSelectedFolderTag(null);
    setFolderTagResults([]);
    setFolderTagResultsError(null);
  }, []);

  // Buscar archivos en todo el bucket
  const searchFiles = useCallback(async (query, fileType = '', minSize = 0, maxSize = Number.MAX_SAFE_INTEGER) => {
    if (!query && !fileType) {
      setFilteredFiles(sortFiles(files, sortBy, sortOrder));
      return;
    }

    setLoading(true);
    try {
      console.log(`🔍 Iniciando búsqueda global para: "${query}"`);
      // Siempre buscar en todo el bucket (searchAll = true)
      const result = await fileService.searchFiles(query, fileType, minSize, maxSize, true);
      const searchResults = sortFiles(filterJunkFiles(result.files || []), sortBy, sortOrder);
      setFilteredFiles(searchResults);
      console.log(`✅ Búsqueda completada: ${searchResults.length} archivos encontrados`);
    } catch (err) {
      setError(err.message);
      console.error('Error buscando archivos:', err);
    } finally {
      setLoading(false);
    }
  }, [files, sortBy, sortOrder]);

  // Cargar archivos y estadísticas cuando el usuario se autentica
  useEffect(() => {
    if (isAuthenticated && !hasLoadedOnce) {
      console.log('🔐 Usuario autenticado, iniciando carga inicial...');
      // Limpiar caché al iniciar para forzar carga fresca
      fileService.clearCache();
      fileService.clearThumbnailCache();
      loadFiles();
      loadStats();
      loadRootFolders(); // Cargar carpetas raíz
    }
  }, [isAuthenticated, loadFiles, loadStats, loadRootFolders, hasLoadedOnce]);

  // Filtrar archivos por path actual
  const filterFilesByPath = useCallback((allFiles, path) => {
    if (!allFiles) return [];
    
    // Solo log importante para debug
    if (path === '') {
      console.log(`🔍 Filtrar archivos raíz: ${allFiles.length} archivos, ${rootFolders.length} rootFolders, loaded: ${rootFoldersLoaded}`);
    }
    
    // En la raíz, esperar a que las carpetas raíz estén cargadas
    if (path === '') {
      if (!rootFoldersLoaded) {
        console.log('⏳ Esperando carga de carpetas raíz...');
        return []; // No mostrar nada hasta que estén cargadas
      }
      
      if (rootFolders.length > 0) {
        console.log('📂 ✅ Usando carpetas raíz de la API');
        
        // Crear objetos de carpetas virtuales desde las carpetas raíz
        const virtualFoldersFromRoot = rootFolders.map(folderName => ({
          Key: folderName + '/',
          displayName: folderName,
          type: 'folder',
          isVirtualFolder: true,
          Size: 0,
          LastModified: new Date().toISOString()
        }));
        
        // Filtrar archivos reales en la raíz (sin prefijos)
        const realItems = allFiles.filter(file => {
          const fileName = file.Key || file.name || '';
          const hasSlash = fileName.includes('/');
          const isRealFolder = fileName.endsWith('/');
          
          if (!hasSlash) {
            // Archivos sin prefijos
            return true;
          } else if (isRealFolder) {
            // NO incluir carpetas reales cuando tenemos carpetas raíz de la API
            // Solo incluir archivos que están dentro de las carpetas raíz
            const folderName = fileName.slice(0, -1);
            if (!folderName.includes('/')) {
              // Es una carpeta de primer nivel, pero no la incluimos si ya tenemos carpetas raíz
              return false;
            }
          }
          return false;
        });
        
        // Combinar carpetas virtuales de la API con items reales
        const result = [...virtualFoldersFromRoot, ...realItems];
        console.log(`✅ Mostrando ${result.length} items: ${rootFolders.length} carpetas + ${realItems.length} archivos`);
        return result;
      } else {
        console.log('📂 ⚠️ Usando extracción de carpetas (sin rootFolders de API)');
      }
    }
    
    // Para otros paths o cuando no tenemos carpetas raíz, usar la lógica original
    // Extraer carpetas virtuales desde prefijos
    const virtualFolders = extractVirtualFolders(allFiles, path);
    
    // Filtrar archivos reales
    const realItems = allFiles.filter(file => {
      const fileName = file.Key || file.name || '';
      
      if (path === '') {
        // En la raíz, mostrar solo archivos sin prefijos y carpetas reales de primer nivel
        const hasSlash = fileName.includes('/');
        const isRealFolder = fileName.endsWith('/');
        
        if (!hasSlash) {
          // Archivos sin prefijos
          return true;
        } else if (isRealFolder) {
          // Solo carpetas de primer nivel (sin slashes en el nombre excepto el final)
          const folderName = fileName.slice(0, -1);
          return !folderName.includes('/');
        }
        return false;
      } else {
        // Mostrar archivos dentro de la carpeta actual
        const normalizedPath = path.endsWith('/') ? path : path + '/';
        
        if (fileName.startsWith(normalizedPath) && fileName !== normalizedPath) {
          const relativePath = fileName.substring(normalizedPath.length);
          
          if (fileName.endsWith('/')) {
            // Es una carpeta - mostrar si es directamente en este nivel
            const folderName = relativePath.slice(0, -1); // remover el slash final
            return !folderName.includes('/'); // no debe tener más slashes = está en el nivel actual
          } else {
            // Es un archivo - mostrar si está directamente en este nivel
            return !relativePath.includes('/');
          }
        }
        
        return false;
      }
    });
    
    // Combinar carpetas virtuales y items reales
    const combinedItems = [...virtualFolders, ...realItems];
    
    // Procesar nombres para display
    return combinedItems.map(file => {
      const fileName = file.Key || file.name || '';
      
      if (file.isVirtualFolder) {
        return file; // Las carpetas virtuales ya tienen displayName
      }
      
      if (path && fileName.startsWith(path)) {
        const normalizedPath = path.endsWith('/') ? path : path + '/';
        const relativeName = fileName.substring(normalizedPath.length);
        return {
          ...file,
          displayName: relativeName.endsWith('/') ? relativeName.slice(0, -1) : relativeName,
          Key: fileName // Mantener la Key original para operaciones
        };
      }
      return {
        ...file,
        displayName: fileName.endsWith('/') ? fileName.slice(0, -1) : fileName
      };
    });
  }, [currentPath, rootFolders, rootFoldersLoaded]);

  // Filtrar y ordenar archivos con búsqueda híbrida instantánea
  useEffect(() => {
    const applyFiltersAndSort = async () => {
      // Si hay ambos tags seleccionados, combinar carpetas y archivos
      let sourceFiles = [];
      if (selectedFolderTag && selectedTag) {
        // Combinar carpetas con tag de carpeta + archivos con tag de archivo dentro de esas carpetas
        sourceFiles = [...folderTagResults, ...tagResults];
      } else if (selectedFolderTag) {
        // Solo tag de carpeta
        sourceFiles = folderTagResults;
      } else if (selectedTag) {
        // Solo tag de archivo
        sourceFiles = tagResults;
      } else {
        // Sin filtros de tag
        sourceFiles = files;
      }

      if (searchTerm.trim()) {
        if (selectedTag || selectedFolderTag) {
          const filtered = filterFiles(sourceFiles, searchTerm, selectedType);
          const sorted = sortFiles(filtered, sortBy, sortOrder);
          setFilteredFiles(sorted);
          return;
        }

        const queryWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);

        const localResults = sourceFiles.filter(file => {
          const fileName = file.Key?.toLowerCase() || '';
          return queryWords.every(word => fileName.includes(word));
        });

        if (localResults.length > 0) {
          console.log(`⚡ Búsqueda local instantánea: "${searchTerm}" (${localResults.length} resultados)`);
          const filteredByType = filterFiles(localResults, '', selectedType);
          const sorted = sortFiles(filteredByType, sortBy, sortOrder);
          setFilteredFiles(sorted);
          setLoading(false);

          const globalSearchTimeout = setTimeout(async () => {
            try {
              if (searchTerm.trim()) {
                const globalResult = await fileService.searchFiles(searchTerm, '', 0, Number.MAX_SAFE_INTEGER, true);
                const globalResults = filterJunkFiles(globalResult.files || []);
                const globalFiltered = filterFiles(globalResults, '', selectedType);
                const globalSorted = sortFiles(globalFiltered, sortBy, sortOrder);

                if (globalSorted.length > localResults.length) {
                  console.log(`🌐 Búsqueda global completada: "${searchTerm}" (${globalSorted.length} resultados totales)`);
                  setFilteredFiles(globalSorted);
                }
              }
            } catch (err) {
              console.warn('Búsqueda global falló, manteniendo resultados locales:', err);
            }
          }, 300);

          return () => clearTimeout(globalSearchTimeout);
        } else {
          console.log(`🔍 Búsqueda global para: "${searchTerm}"`);
          setLoading(true);
          try {
            const result = await fileService.searchFiles(searchTerm, '', 0, Number.MAX_SAFE_INTEGER, true);
            const searchResults = filterJunkFiles(result.files || []);
            const filteredByType = filterFiles(searchResults, '', selectedType);
            const sorted = sortFiles(filteredByType, sortBy, sortOrder);
            setFilteredFiles(sorted);
            console.log(`✅ Búsqueda global completada: ${sorted.length} resultados para "${searchTerm}"`);
          } catch (err) {
            console.error('Error en búsqueda global:', err);
            setError(err.message);
            setFilteredFiles([]);
          } finally {
            setLoading(false);
          }
        }
      } else {
        // Si hay filtros de tag, no aplicar filtro de path (ya están filtrados)
        if (selectedTag || selectedFolderTag) {
          const sorted = sortFiles(sourceFiles, sortBy, sortOrder);
          setFilteredFiles(sorted);
        } else {
          const pathFiltered = filterFilesByPath(sourceFiles, currentPath);
          const searchFiltered = filterFiles(pathFiltered, '', selectedType);
          const sorted = sortFiles(searchFiltered, sortBy, sortOrder);
          setFilteredFiles(sorted);
        }
      }
    };

    applyFiltersAndSort();
  }, [files, tagResults, folderTagResults, selectedTag, selectedFolderTag, searchTerm, selectedType, sortBy, sortOrder, currentPath, filterFilesByPath]);


  // Efecto específico para cuando cambian las rootFolders
  useEffect(() => {
    if (rootFoldersLoaded && currentPath === '' && !searchTerm.trim()) {
      console.log('🔄 rootFolders cargadas completamente, forzando actualización de filteredFiles...');
      const pathFiltered = filterFilesByPath(files, currentPath);
      const searchFiltered = filterFiles(pathFiltered, '', selectedType);
      const sorted = sortFiles(searchFiltered, sortBy, sortOrder);
      setFilteredFiles(sorted);
      console.log(`✅ Actualización por rootFolders completada: ${sorted.length} items`);
    }
  }, [rootFoldersLoaded, rootFolders, files, currentPath, searchTerm, selectedType, sortBy, sortOrder, filterFilesByPath]);

  // Crear carpeta
  const createFolder = useCallback(async (folderName, tags = null) => {
    if (!isAuthenticated) return false;
    
    setLoading(true);
    try {
      // Crear carpeta en el path actual
      const fullFolderPath = currentPath ? `${currentPath}${folderName}` : folderName;
      console.log(`📁 Creando carpeta: "${fullFolderPath}" en path actual: "${currentPath}"`);
      await fileService.createFolder(fullFolderPath, tags);
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Recargar archivos de la carpeta actual (forzando recarga fresca)
      await loadFiles(currentPath, true);
      if (selectedTag) {
        await loadFilesByTag(selectedTag);
      }
      await loadTags();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error creando carpeta:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, loadFiles, currentPath, selectedTag, loadFilesByTag, loadTags]);

  // Subir archivo
  const uploadFile = useCallback(async (fileName, base64Data, mimeType, tags = null, reloadFiles = true, onUploadProgress = null) => {
    if (!isAuthenticated) return false;
    
    setLoading(true);
    try {
      // Determinar la ruta completa del archivo
      // Si fileName ya incluye el currentPath (tiene estructura de carpetas), usarlo tal cual
      // Si no, combinarlo con currentPath
      let fullFileName;
      if (currentPath && fileName.startsWith(currentPath)) {
        // Ya incluye el path actual, usar tal cual
        fullFileName = fileName;
      } else if (currentPath) {
        // Combinar con currentPath
        const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath : currentPath + '/';
        fullFileName = normalizedCurrentPath + fileName;
      } else {
        // No hay currentPath, usar fileName tal cual
        fullFileName = fileName;
      }
      
      console.log(`📤 Subiendo archivo: "${fullFileName}" en path actual: "${currentPath}"`);
      await fileService.uploadBase64(fullFileName, base64Data, mimeType, tags, onUploadProgress);
      
      // Limpiar caché del frontend para todas las carpetas afectadas
      // Si el archivo está en una subcarpeta, limpiar también las carpetas padre
      const pathParts = fullFileName.split('/').filter(p => p);
      if (pathParts.length > 1) {
        // Limpiar caché de todas las carpetas en la ruta
        for (let i = 0; i < pathParts.length - 1; i++) {
          const folderPath = pathParts.slice(0, i + 1).join('/') + '/';
          fileService.clearCache(folderPath);
        }
      }
      // También limpiar el currentPath
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Solo recargar archivos si se solicita (por defecto sí, para mantener compatibilidad)
      if (reloadFiles) {
        await loadFiles(currentPath, true);
        if (selectedTag) {
          await loadFilesByTag(selectedTag);
        }
        await loadTags();
      }
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error subiendo archivo:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentPath, loadFiles, selectedTag, loadFilesByTag, loadTags, setError]);

  // Eliminar archivo
  const deleteFile = useCallback(async (fileName) => {
    setLoading(true);
    try {
      console.log(`🗑️ Eliminando archivo: "${fileName}"`);
      await fileService.deleteFile(fileName);
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Recargar archivos de la carpeta actual (forzando recarga fresca)
      await loadFiles(currentPath, true);
      if (selectedTag) {
        await loadFilesByTag(selectedTag);
      }
      await loadTags();
      setSelectedFiles(prev => prev.filter(f => f !== fileName));
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error eliminando archivo:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadFiles, currentPath, selectedTag, loadFilesByTag, loadTags]);

  // Eliminar múltiples archivos
  const deleteMultipleFiles = useCallback(async (fileNames) => {
    setLoading(true);
    try {
      console.log(`🗑️ Eliminando ${fileNames.length} archivos`);
      await fileService.deleteMultipleFiles(fileNames);
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Recargar archivos de la carpeta actual (forzando recarga fresca)
      await loadFiles(currentPath, true);
      if (selectedTag) {
        await loadFilesByTag(selectedTag);
      }
      await loadTags();
      setSelectedFiles([]);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error eliminando archivos:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadFiles, currentPath, selectedTag, loadFilesByTag, loadTags]);

  // Eliminar carpeta
  const deleteFolder = useCallback(async (folderPath) => {
    setLoading(true);
    try {
      console.log(`🗑️ Eliminando carpeta: "${folderPath}"`);
      await fileService.deleteFolder(folderPath);
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Recargar archivos de la carpeta actual (forzando recarga fresca)
      await loadFiles(currentPath, true);
      if (selectedTag) {
        await loadFilesByTag(selectedTag);
      }
      await loadTags();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error eliminando carpeta:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadFiles, currentPath, selectedTag, loadFilesByTag, loadTags]);

  // Renombrar archivo
  const renameFile = useCallback(async (oldFileName, newFileName) => {
    setLoading(true);
    try {
      console.log(`✏️ Renombrando archivo: "${oldFileName}" → "${newFileName}"`);
      await fileService.renameFile(oldFileName, newFileName);
      
      // Limpiar caché del frontend para forzar recarga fresca
      fileService.clearCache(currentPath);
      console.log(`🗑️ Caché del frontend limpiado para carpeta: "${currentPath || 'root'}"`);
      
      // Recargar archivos de la carpeta actual (forzando recarga fresca)
      await loadFiles(currentPath, true);
      if (selectedTag) {
        await loadFilesByTag(selectedTag);
      }
      await loadTags();
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error renombrando archivo:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadFiles, currentPath, selectedTag, loadFilesByTag, loadTags]);

  // Obtener URL de archivo
  const getFileUrl = useCallback(async (fileName, download = false) => {
    try {
      const result = await fileService.getFileUrl(fileName, download);
      return result.url;
    } catch (err) {
      console.error('Error obteniendo URL:', err);
      return null;
    }
  }, []);

  // Seleccionar/deseleccionar archivo
  const toggleFileSelection = useCallback((fileName) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileName)) {
        return prev.filter(f => f !== fileName);
      } else {
        return [...prev, fileName];
      }
    });
  }, []);

  // Seleccionar todos los archivos
  const selectAllFiles = useCallback(() => {
    const allFileNames = filteredFiles.map(file => file.Key || file.name);
    setSelectedFiles(allFileNames);
  }, [filteredFiles]);

  // Deseleccionar todos los archivos
  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  // Navegación de carpetas
  const navigateToFolder = useCallback(async (folderName) => {
    let newPath;
    if (!folderName) {
      newPath = '';
    } else {
      // Si es un path absoluto (contiene '/' y NO empieza con currentPath), usar tal cual
      if (folderName.includes('/') && !folderName.startsWith(currentPath)) {
        newPath = folderName.endsWith('/') ? folderName : folderName + '/';
      } else if (folderName.includes('/') && folderName.startsWith(currentPath)) {
        // Si ya es un path completo (contiene el currentPath), usarlo tal como está
        newPath = folderName.endsWith('/') ? folderName : folderName + '/';
      } else {
        // Si es un nombre relativo, construir el path completo
        const basePath = currentPath || '';
        newPath = basePath + folderName + '/';
      }
    }
    setCurrentPath(newPath);
    setNavigationHistory(prev => [...prev, newPath]);
    clearSelection();
    setError(null);
    // Limpiar filtros de tags al navegar
    if (selectedTag) {
      clearTagFilter();
    }
    if (selectedFolderTag) {
      clearFolderTagFilter();
    }
    await Promise.all([
      loadFiles(newPath),
      loadStats()
    ]);
  }, [clearSelection, loadFiles, loadStats, setError, currentPath, selectedTag, selectedFolderTag, clearTagFilter, clearFolderTagFilter]);

  const navigateBack = useCallback(async () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remover el path actual
      const previousPath = newHistory[newHistory.length - 1];
      setCurrentPath(previousPath);
      setNavigationHistory(newHistory);
      clearSelection();
      // Limpiar filtros de tags al navegar hacia atrás
      if (selectedTag) {
        clearTagFilter();
      }
      if (selectedFolderTag) {
        clearFolderTagFilter();
      }
      
      // Cargar archivos de la carpeta anterior
      await loadFiles(previousPath);
    }
  }, [navigationHistory, clearSelection, loadFiles, selectedTag, selectedFolderTag, clearTagFilter, clearFolderTagFilter]);

  const navigateToRoot = useCallback(async () => {
    setCurrentPath('');
    setNavigationHistory(['']);
    clearSelection();
    // Limpiar filtros de tags al navegar a la raíz
    if (selectedTag) {
      clearTagFilter();
    }
    if (selectedFolderTag) {
      clearFolderTagFilter();
    }
    
    // Cargar archivos de la raíz
    await loadFiles('');
  }, [clearSelection, loadFiles, selectedTag, selectedFolderTag, clearTagFilter, clearFolderTagFilter]);

  const navigateToPath = useCallback((path) => {
    setCurrentPath(path);
    // Reconstruir historial hasta este path
    const pathParts = path.split('/').filter(part => part !== '');
    const newHistory = [''];
    let currentBuiltPath = '';
    pathParts.forEach(part => {
      currentBuiltPath += part + '/';
      newHistory.push(currentBuiltPath);
    });
    setNavigationHistory(newHistory);
    clearSelection();
    // Limpiar filtros de tags al navegar a un path específico
    if (selectedTag) {
      clearTagFilter();
    }
    if (selectedFolderTag) {
      clearFolderTagFilter();
    }
  }, [clearSelection, selectedTag, selectedFolderTag, clearTagFilter, clearFolderTagFilter]);

  // Recargar archivos y stats cada vez que cambia el path
  useEffect(() => {
    if (currentPath !== undefined) {
      loadFiles(currentPath);
      loadStats();
    }
  }, [currentPath, loadFiles, loadStats]);

  return {
    // Estado
    files,
    filteredFiles,
    loading,
    error,
    searchTerm,
    selectedType,
    sortBy,
    sortOrder,
    selectedFiles,
    stats: localStats,
    currentPath,
    navigationHistory,
    fromCache,
    rootFolders,
    rootFoldersLoading,
    rootFoldersLoaded,
    tags,
    tagsLoading,
    selectedTag,
    tagResultsLoading,
    tagResultsError,
    selectedFolderTag,
    folderTagResultsLoading,
    folderTagResultsError,

    // Acciones
    setSearchTerm,
    setSelectedType,
    setSortBy,
    setSortOrder,
    loadFiles,
    loadRootFolders,
    searchFiles,
    createFolder,
    uploadFile,
    deleteFile,
    deleteMultipleFiles,
    deleteFolder,
    renameFile,
    getFileUrl,
    toggleFileSelection,
    selectAllFiles,
    clearSelection,
    setError,
    loadTags,
    loadFilesByTag,
    clearTagFilter,
    loadFilesByFolderTag,
    clearFolderTagFilter,
    
    // Navegación
    navigateToFolder,
    navigateBack,
    navigateToRoot,
    navigateToPath
  };
}; 